/**
 * AdminSchemaDrift - lists widgets whose underlying dataset schemas or semantic keys
 * no longer resolve. Admins run a scan on demand and see a table of drifts by
 * severity + dashboard + widget so they know exactly what needs fixing.
 */
import React, { useEffect, useState } from "react";import { AlertTriangle, RefreshCw, CheckCircle2, ShieldCheck, Loader2, ExternalLink, Wand2, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";

export function AdminSchemaDrift() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulking, setBulking] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadLatest = async () => {
    try { const { data } = await api.get("/schema-drift"); setScan(data); }
    catch (e) { /* first time -> empty */ }
    setLoading(false);
  };
  useEffect(() => { loadLatest(); }, []);

  const runScan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/schema-drift/scan");
      setScan(data);
      toast.success(`Scan complete: ${data.drift_count} issue(s) found across ${data.dashboards_scanned} dashboards`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setBusy(false); }
  };

  const applyAll = async () => {
    if (!scan?.drifts?.length) return;
    const items = scan.drifts
      .map((d, i) => ({ d, i }))
      .filter(({ d }) => !d.applied && d.suggested_fix
        && ((Object.keys(d.suggested_fix.fields || {}).length + Object.keys(d.suggested_fix.keys || {}).length) > 0))
      .map(({ d, i }) => ({
        _index: i,
        dashboard_id: d.dashboard_id,
        widget_id: d.widget_id,
        fields: d.suggested_fix.fields || {},
        keys: d.suggested_fix.keys || {},
      }));
    if (items.length === 0) {
      toast.message("Nothing to apply — no drifts have suggested fixes");
      return;
    }
    setBulking(true);
    try {
      // Strip the client-side _index before sending
      const payload = { items: items.map(({ _index, ...rest }) => rest) };
      const { data } = await api.post("/schema-drift/apply-all", payload);
      const appliedIdx = new Set();
      data.results.forEach((r, i) => { if (r.ok) appliedIdx.add(items[i]._index); });
      setScan((cur) => cur ? {
        ...cur,
        drifts: cur.drifts.map((x, idx) => appliedIdx.has(idx) ? { ...x, applied: true } : x),
      } : cur);
      if (data.failed > 0) {
        toast.warning(`Applied ${data.applied}/${data.total}. ${data.failed} failed — check row-level errors.`);
      } else {
        toast.success(`Applied all ${data.applied} suggested fix(es).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk apply failed");
    } finally { setBulking(false); }
  };

  const sevColor = (s) => (s === "high" ? "text-[color:var(--danger)] border-[color:var(--danger)]/60" :
                          s === "medium" ? "text-[color:var(--warning)] border-[color:var(--warning)]/60" :
                          "text-[color:var(--ink-3)] border-[color:var(--glass-border)]");

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand-ocean)]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] px-4 py-3">
        <div>
          <div className="flex items-center gap-2 font-display text-lg text-[color:var(--ink)]">
            <ShieldCheck className="w-5 h-5 text-[color:var(--brand-ocean)]" />
            Schema Drift Monitor
          </div>
          <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
            Scans every dashboard visible to your tenant and flags widgets whose columns no longer exist in the source database or semantic model.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scan?.drifts?.some((d) => !d.applied && d.suggested_fix && (Object.keys(d.suggested_fix.fields || {}).length + Object.keys(d.suggested_fix.keys || {}).length) > 0) && (
            <Button data-testid="drift-apply-all" onClick={applyAll} disabled={bulking || busy} variant="outline" className="border-[color:var(--brand-mint)] text-[color:var(--brand-mint)] hover:bg-[color:rgba(0,194,168,0.10)]">
              {bulking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
              {bulking ? "Applying…" : "Apply all suggested fixes"}
            </Button>
          )}
          <Button data-testid="drift-scan" onClick={runScan} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            {busy ? "Scanning…" : "Run scan"}
          </Button>
        </div>
      </div>

      {!scan && (
        <div className="text-center text-sm text-[color:var(--ink-3)] py-10">
          No scans yet. Click <b>Run scan</b> to check your dashboards.
        </div>
      )}

      {scan && (
        <>
          <div className="flex items-center gap-6 text-xs text-[color:var(--ink-2)]">
            <div><span className="text-[color:var(--ink-3)]">Scanned:</span> <b>{scan.dashboards_scanned}</b> dashboards</div>
            <div><span className="text-[color:var(--ink-3)]">Issues:</span> <b data-testid="drift-count">{scan.drift_count}</b></div>
            <div><span className="text-[color:var(--ink-3)]">At:</span> {new Date(scan.scanned_at).toLocaleString()}</div>
          </div>

          {scan.drift_count === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-[color:var(--success)]/40 bg-[color:rgba(0,194,168,0.06)] text-[color:var(--success)] px-4 py-3 text-sm">
              <CheckCircle2 className="w-5 h-5" />
              All widgets resolve cleanly against their current schemas. Nice.
            </div>
          ) : (
            <div data-testid="drift-list" className="rounded-xl border border-[color:var(--glass-border)] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--surface-2)] text-[color:var(--ink-3)]">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Severity</th>
                    <th className="px-3 py-2 font-medium">Dashboard</th>
                    <th className="px-3 py-2 font-medium">Widget</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                    <th className="px-3 py-2 font-medium">Suggested fix</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {scan.drifts.map((d, i) => (
                    <DriftRow
                      key={i}
                      idx={i}
                      drift={d}
                      sevColor={sevColor}
                      onApplied={() => setScan((cur) => cur ? { ...cur, drifts: cur.drifts.map((x, xi) => xi === i ? { ...x, applied: true } : x) } : cur)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DriftRow({ idx, drift, sevColor, onApplied }) {
  const [busy, setBusy] = React.useState(false);
  // Derive `applied` from the prop so it stays in sync when the parent flips
  // `drifts[i].applied` after a bulk-apply. Local single-row apply also works
  // because it calls onApplied() which updates the parent state.
  const applied = !!drift.applied;
  const sf = drift.suggested_fix || {};
  const fields = sf.fields || {};
  const keys = sf.keys || {};
  const hasSuggestion = Object.keys(fields).length > 0 || Object.keys(keys).length > 0;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/schema-drift/apply", {
        dashboard_id: drift.dashboard_id,
        widget_id: drift.widget_id,
        fields, keys,
      });
      onApplied();
      toast.success(`Fixed ${data.changed} reference(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-t border-[color:var(--glass-border)]">
      <td className="px-3 py-2">
        <Badge variant="outline" className={"uppercase text-[10px] " + sevColor(drift.severity)}>
          <AlertTriangle className="w-3 h-3 mr-1" />{drift.severity}
        </Badge>
      </td>
      <td className="px-3 py-2 text-[color:var(--ink-2)]">{drift.dashboard_name}</td>
      <td className="px-3 py-2 text-[color:var(--ink-2)]">{drift.widget_title}</td>
      <td className="px-3 py-2 text-xs text-[color:var(--ink-3)] max-w-[380px]">{drift.reason}</td>
      <td className="px-3 py-2 text-xs">
        {hasSuggestion ? (
          <div className="space-y-0.5 font-mono text-[10px]">
            {Object.entries(fields).map(([o, n]) => (
              <div key={"f" + o} className="text-[color:var(--ink-2)] flex items-center gap-1">
                <span className="text-[color:var(--danger)] line-through">{o}</span>
                <ArrowRight className="w-3 h-3 text-[color:var(--ink-3)]" />
                <span className="text-[color:var(--success)]">{n}</span>
              </div>
            ))}
            {Object.entries(keys).map(([o, n]) => (
              <div key={"k" + o} className="text-[color:var(--ink-2)] flex items-center gap-1">
                <span className="text-[color:var(--danger)] line-through">{o}</span>
                <ArrowRight className="w-3 h-3 text-[color:var(--ink-3)]" />
                <span className="text-[color:var(--success)]">{n}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-[color:var(--ink-3)] italic">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          {hasSuggestion && !applied && (
            <Button
              data-testid={`drift-apply-${idx}`}
              size="sm"
              onClick={apply}
              disabled={busy}
              className="h-7 bg-[color:var(--brand-mint)] text-white hover:brightness-95"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
              {busy ? "" : "Apply"}
            </Button>
          )}
          {applied && (
            <span data-testid={`drift-applied-${idx}`} className="text-[color:var(--success)] text-[11px] inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />Applied
            </span>
          )}
          <a
            href={`/builder/${drift.dashboard_id}`}
            className="text-[color:var(--brand-ocean)] hover:underline text-xs inline-flex items-center gap-1"
            data-testid={`drift-open-${idx}`}
          >Open <ExternalLink className="w-3 h-3" /></a>
        </div>
      </td>
    </tr>
  );
}

